import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, Loader2, Sparkles, ShieldCheck, QrCode, Globe2, Printer } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { openCheckout } from "@/lib/razorpay";
import { GoogleButton } from "@/components/AuthCallback";
import { Brand } from "@/components/AppHeader";

const months = (n) => `${n} month${n > 1 ? "s" : ""}`;
const PERKS = [[Printer, "Print PVC certificates & XRF reports"], [QrCode, "Auto certificate numbers + QR verify"], [Globe2, "Your own showroom website"], [ShieldCheck, "Customer history & bulk import"]];

const PlanCard = ({ p, on, onPick, i }) => (
  <button type="button" onClick={onPick} data-testid={`join-plan-${i}`}
    className={`relative text-left rounded-xl border-2 p-4 transition-all ${on ? "bg-amber-500/10 -translate-y-0.5" : "border-slate-800 hover:border-slate-600"}`}
    style={on ? { borderColor: p.color } : undefined}>
    {on && <Check size={14} className="absolute top-3 right-3" style={{ color: p.color }} />}
    <p className="font-heading font-extrabold" style={{ color: p.color }}>{p.name}</p>
    <p className="text-2xl font-bold mt-1">{p.trial ? "Free" : `₹${p.amount.toLocaleString("en-IN")}`}</p>
    <p className="text-[11px] text-slate-400 mt-1">{p.trial ? `${p.card_limit} cards · view & print only` : `${p.card_limit ? `${p.card_limit.toLocaleString("en-IN")} cards` : "Unlimited cards"} · ${months(p.duration_months)}`}</p>
  </button>
);

export default function Join() {
  const { user, acceptAuth } = useAuth();
  const nav = useNavigate();
  const [opts, setOpts] = useState(null);
  const [pick, setPick] = useState("trial");
  const [f, setF] = useState({ store_name: "", name: "", phone: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState("");
  useEffect(() => { api.get("/public/join-options").then((r) => setOpts(r.data)); }, []);
  if (user) return <Navigate to={user.role === "superadmin" ? "/admin" : "/"} replace />;

  const plans = opts ? [{ id: "trial", name: "Free trial", trial: true, card_limit: opts.trial_cards, color: "#34D399" }, ...opts.plans] : [];
  const chosen = plans.find((p) => p.id === pick);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (chosen.trial) {
        const { data } = await api.post("/public/join/trial", { ...f, plan_id: "trial" });
        if (data.pending) return setDone(data.message);
        await acceptAuth(data);
        toast.success("Welcome! Your free trial has started");
        return nav("/");
      }
      const { data: order } = await api.post("/public/join/order", { ...f, plan_id: pick });
      const pay = await openCheckout(order, { name: "PVC Card Studio", description: `${order.plan_name} plan` });
      const { data } = await api.post("/public/join/verify", { ref: order.ref, ...pay });
      await acceptAuth(data);
      toast.success(`Payment successful — ${order.plan_name} plan is active`);
      nav("/");
    } catch (err) { toast.error(err.response ? errMsg(err) : err.message); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-950 grid lg:grid-cols-[1fr_1.15fr]" data-testid="join-page">
      <aside className="hidden lg:flex flex-col justify-between p-12 border-r border-slate-800 bg-[radial-gradient(ellipse_at_top_left,rgba(245,158,11,0.12),transparent_60%)]">
        <Brand />
        <div className="space-y-8">
          <p className="text-[11px] font-mono uppercase tracking-[0.25em] text-amber-400">For jewellers</p>
          <h1 className="font-heading text-5xl font-extrabold leading-[1.05]">Certificates your customers can scan and trust.</h1>
          <ul className="space-y-4">{PERKS.map(([Icon, t]) => <li key={t} className="flex items-center gap-3 text-slate-300"><Icon size={18} className="text-amber-400" /> {t}</li>)}</ul>
        </div>
        <p className="text-xs text-slate-500">Secure payments by Razorpay · UPI, cards & netbanking</p>
      </aside>

      <main className="p-6 sm:p-12 flex items-center">
        <div className="w-full max-w-xl mx-auto space-y-8">
          <div>
            <h2 className="font-heading text-3xl font-extrabold">Start your jewellery studio</h2>
            <p className="text-sm text-slate-400 mt-2">Already have an account? <Link to="/login" className="text-amber-400 font-semibold" data-testid="join-login-link">Sign in</Link></p>
          </div>
          {done ? (
            <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-6 text-emerald-200" data-testid="join-pending">{done}</div>
          ) : !opts ? <Loader2 className="animate-spin text-amber-400" /> : !opts.signup_open ? (
            <p className="rounded-xl border border-slate-800 p-6 text-slate-400" data-testid="join-closed">New sign-ups are closed. Please contact the administrator.</p>
          ) : (
            <form onSubmit={submit} className="space-y-6">
              <div className="space-y-2">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">1 · Choose your plan</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">{plans.map((p, i) => <PlanCard key={p.id} p={p} i={i} on={pick === p.id} onPick={() => setPick(p.id)} />)}</div>
                {chosen?.trial && <p className="text-[11px] text-slate-500" data-testid="join-trial-note"><Sparkles size={11} className="inline mr-1 text-emerald-400" />Trial includes {opts.trial_cards} cards. Editing and deleting cards unlock when you subscribe.{opts.trial_needs_approval && " Trial accounts are activated after approval."}</p>}
                {!chosen?.trial && !opts.payments_enabled && <p className="text-[11px] text-orange-300" data-testid="join-payments-off">Online payment isn't switched on yet — start with the free trial for now.</p>}
              </div>
              <div className="space-y-3">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">2 · Your store</p>
                {chosen?.trial && <>
                  <GoogleButton label="Start free trial with Google" />
                  <div className="flex items-center gap-3 text-[11px] uppercase tracking-wider text-slate-500"><span className="h-px flex-1 bg-slate-800" />or with email<span className="h-px flex-1 bg-slate-800" /></div>
                </>}
                <div className="grid sm:grid-cols-2 gap-3">
                  <input required className="cf-input h-11" placeholder="Store name" value={f.store_name} onChange={set("store_name")} data-testid="join-store-input" />
                  <input className="cf-input h-11" placeholder="Owner name" value={f.name} onChange={set("name")} data-testid="join-name-input" />
                  <input className="cf-input h-11" placeholder="Mobile number" value={f.phone} onChange={set("phone")} data-testid="join-phone-input" />
                  <input required type="email" className="cf-input h-11" placeholder="Email (login ID)" value={f.email} onChange={set("email")} data-testid="join-email-input" />
                </div>
                <input required type="password" minLength={6} className="cf-input h-11" placeholder="Password (min 6 characters)" value={f.password} onChange={set("password")} data-testid="join-password-input" />
              </div>
              <button disabled={busy || (!chosen?.trial && !opts.payments_enabled)} className="cf-btn-primary w-full h-12 text-base" data-testid="join-submit-button">
                {busy && <Loader2 size={16} className="animate-spin" />}
                {chosen?.trial ? "Start free trial" : `Pay ₹${chosen?.amount.toLocaleString("en-IN")} & start ${chosen?.name}`}
              </button>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}
